import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type {
  Exhibitor,
  DirectVerificationResponse,
  VerifiedExhibitorsResponse,
  ExhibitorStatus,
  Participation,
  StaffRegistration,
  Staff,
  CarRegistration,
  Car,
  IDDocumentType,
  StaffDetail,
  StaffUpdate,
} from "../Component/Application/exhibitorsaccess/types"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: { "Content-Type": "application/json" },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken")
  if (token) config.headers!["Authorization"] = `Bearer ${token}`
    Object.assign(config.headers, getExhibitionHeader());
  return config
})

export const ExhibitorRegistrationService = {
  // Exhibitor selection and verification
  getExhibitorsForSelection: async (): Promise<Exhibitor[]> => {
    const response = await api.get("/api/exhibitorsaccess/exhibitors/selection/")
    return response.data
  },

  // Single endpoint for direct verification - creates both models and returns success
  directVerifyExhibitor: async (data: { exhibitor_id: number }): Promise<DirectVerificationResponse> => {
    const response = await api.post("/api/exhibitorsaccess/exhibitors/direct-verify/", data)
    return response.data
  },

  getVerifiedExhibitorsWithAccess: async (): Promise<VerifiedExhibitorsResponse> => {
    const response = await api.get("/api/exhibitorsaccess/exhibitors/verified-with-access/")
    return response.data
  },

  getExhibitorStatus: async (exhibitorId: number): Promise<ExhibitorStatus> => {
    const response = await api.get(`/api/exhibitorsaccess/exhibitors/${exhibitorId}/status/`)
    console.log("Raw API Response for exhibitor status:", response.data)
    const data = response.data

    // Process participations data to ensure proper number conversion
    if (data.participations && Array.isArray(data.participations)) {
      data.participations = data.participations.map((participation: any) => {
        const processed = {
          id: Number(participation.id) || 0,
          exhibition_title: participation.exhibition_title || "",
          stand_category_name: participation.stand_category_name || "",
          current_representatives: Number(participation.current_representatives) || 0,
          max_representatives: Number(participation.max_representatives) || 0,
          current_cars: Number(participation.current_cars) || 0,
          max_cars: Number(participation.max_cars) || 0,
          can_add_representatives: Boolean(participation.can_add_representatives),
          can_add_cars: Boolean(participation.can_add_cars),
        }
        console.log(`Processed participation ${processed.id}:`, processed)
        return processed
      })
    } else {
      console.log("No participations found or participations is not an array")
      data.participations = []
    }

    console.log("Final processed data:", data)
    return data
  },

  registerSelfAsStaff: async (
    exhibitorId: number,
  ): Promise<{ message: string; representation_id: number; participation_id: number }> => {
    const response = await api.post(`/api/exhibitorsaccess/exhibitors/${exhibitorId}/register-self/`)
    return response.data
  },

  getExhibitorParticipations: async (exhibitorId: number): Promise<Participation[]> => {
    const status = await ExhibitorRegistrationService.getExhibitorStatus(exhibitorId)
    console.log("Getting participations from status:", status.participations)
    return status.participations
  },

  // Staff management with file uploads
  registerStaff: async (data: StaffRegistration): Promise<{ message: string; id: number }> => {
    const formData = new FormData()
    Object.keys(data).forEach((key) => {
      const value = data[key as keyof StaffRegistration]
      if (value !== null && value !== undefined && value !== "") {
        if (key === "id_doc_attachment" || key === "photo") {
          if (value instanceof File) {
            formData.append(key, value)
          }
        } else {
          formData.append(key, String(value))
        }
      }
    })

    const response = await api.post("/api/exhibitorsaccess/staff/register/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data
  },

  getParticipationStaff: async (participationId: number): Promise<Staff[]> => {
    const response = await api.get(`/api/exhibitorsaccess/participations/${participationId}/staff/`)
    return response.data
  },

  removeStaff: async (representationId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/exhibitorsaccess/staff/${representationId}/remove/`)
    return response.data
  },

  // Car management
  registerCar: async (participationId: number, data: CarRegistration): Promise<{ message: string; id: number }> => {
    const response = await api.post(`/api/exhibitorsaccess/participations/${participationId}/cars/register/`, data)
    return response.data
  },

  getParticipationCars: async (participationId: number): Promise<Car[]> => {
    const response = await api.get(`/api/exhibitorsaccess/participations/${participationId}/cars/`)
    return response.data
  },

  removeCar: async (carId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/exhibitorsaccess/cars/${carId}/remove/`)
    return response.data
  },

  async updateCar(id: number, data: CarRegistration): Promise<Car> {
    const response = await api.put(`/api/exhibitorsaccess/cars/${id}/update/`, data)
    return response.data
  },

  // Utility
  getIDDocumentTypes: async (): Promise<IDDocumentType[]> => {
    const response = await api.get("/api/accounts/get-id-document-types/")
    return response.data
  },

  getStaffDetail: async (representationId: number): Promise<StaffDetail> => {
    const response = await api.get(`/api/exhibitorsaccess/staff/${representationId}/detail/`)
    return response.data
  },

  updateStaff: async (representationId: number, data: StaffUpdate): Promise<{ message: string; id: number }> => {
    const formData = new FormData()
    Object.keys(data).forEach((key) => {
      const value = data[key as keyof StaffUpdate]
      if (value !== null && value !== undefined && value !== "") {
        if (key === "id_doc_attachment" || key === "photo") {
          if (value instanceof File) {
            formData.append(key, value)
          }
        } else if (key === "remove_photo" || key === "remove_id_doc") {
          formData.append(key, String(value))
        } else {
          formData.append(key, String(value))
        }
      }
    })

    const response = await api.put(`/api/exhibitorsaccess/staff/${representationId}/update/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data
  },

  partialUpdateStaff: async (
    representationId: number,
    data: Partial<StaffUpdate>,
  ): Promise<{ message: string; id: number }> => {
    const formData = new FormData()
    Object.keys(data).forEach((key) => {
      const value = data[key as keyof StaffUpdate]
      if (value !== null && value !== undefined) {
        if (key === "id_doc_attachment" || key === "photo") {
          if (value instanceof File) {
            formData.append(key, value)
          }
        } else {
          formData.append(key, String(value))
        }
      }
    })

    const response = await api.patch(`/api/exhibitorsaccess/staff/${representationId}/update/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data
  },
}
